import {
	Component,
	ElementRef,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
	ViewChild,
	ViewEncapsulation,
	HostListener,
	ChangeDetectorRef
} from '@angular/core';
import { Subscription } from 'rxjs';
import { IdService } from '../../shared/services/id.service';
import { PopoverService } from '../../shared/services/popover.service';
import { TTooltipPosition, TTooltipPositionX, TTooltipSize, TTooltipTransition } from './tooltip.types';

@Component({
	selector: 'rbc-tooltip',
	templateUrl: './tooltip.component.html',
	styleUrls: ['./tooltip.component.scss'],
	providers: [PopoverService],
	encapsulation: ViewEncapsulation.None
})
export class TooltipComponent implements OnInit, OnDestroy, OnChanges {
	@Input() active: boolean;
	@Input() clickable: boolean = true;
	@Input() hover: false = false;
	@Input() icon?: string = 'question-circle';
	@Input() iconWeight?: 'solid' | 'regular' | 'light' = 'regular';
	@Input() id: string;
	@Input() labelTrigger: string = 'Toggle tooltip';
	@Input() message: string;
	@Input() position?: TTooltipPosition = 'bottom';
	@Input() positionX?: TTooltipPositionX = 'center';
	_size: TTooltipSize = 'small';
	@Input()
	set size(i: TTooltipSize) {
		this._size = i;
	}
	get size(): TTooltipSize {
		return this._size || 'small';
	}
	@Input() title: string;
	@Input() transition?: TTooltipTransition = 'opacity-transform';
	@Input() absolute: boolean = false;

	get isOpen(): boolean {
		return this.popoverService.isOpen || this.active;
	}
	set isOpen(change: boolean) {
		this.popoverService.isOpen = change;
	}
	get topGen(): number {
		return this.popoverService.topGen;
	}
	get leftGen(): number {
		return this.popoverService.leftGen;
	}
	get overflowLeft(): number {
		return this.popoverService.overflowLeft;
	}

	@ViewChild('self') self: ElementRef;
	@ViewChild('content') content: ElementRef;
	@ViewChild('contentSlot') contentSlot: ElementRef;
	@ViewChild('triggerSlot') triggerSlot: ElementRef;

	@HostListener('document:click', ['$event'])
	clickout(event) {
		if (!this.eRef.nativeElement.contains(event.target) && this.isOpen) {
			this.isOpen = false;
			this.cdr.markForCheck();
		}
	}

	@HostListener('document:touchend', ['$event'])
	touchend(event) {
		if (!this.eRef.nativeElement.contains(event.target) && this.isOpen) {
			this.isOpen = false;
			this.cdr.markForCheck();
		}
	}

	get contentSlotExists(): boolean {
		// make sure it's not a angular comment
		return this.contentSlot ? !!this.contentSlot.nativeElement.children.length : false;
	}
	get triggerSlotExists(): boolean {
		// make sure it's not a angular comment
		return this.triggerSlot ? !!(this.triggerSlot.nativeElement as HTMLElement).children.length : false;
	}

	$popoverToggleSub: Subscription;

	constructor(
		private idService: IdService,
		private popoverService: PopoverService,
		private cdr: ChangeDetectorRef,
		private eRef: ElementRef
	) {
		this.id = this.idService.generate();
	}

	ngOnInit() {
		this.popoverService.syncProps({
			clickable: this.clickable,
			active: this.active,
			hover: this.hover,
			position: this.position,
			positionX: this.positionX,
			offset: 10,
			triggerRef: (this.self.nativeElement as HTMLElement).firstElementChild,
			popoverRef: this.content.nativeElement
		});

		// sync with isOpen changes in service
		// if (this.popoverService.$popoverToggle) {
		// 	this.$popoverToggleSub = this.popoverService.$popoverToggle.subscribe(change => {
		// 		this.isOpen = change;
		// 	});
		// }
	}

	ngOnDestroy(): void {
		if (this.idService) {
			this.idService.remove(this.id);
		}
		if (this.$popoverToggleSub) {
			this.$popoverToggleSub.unsubscribe();
		}

		// ensure tooltip is fully removed
		this.isOpen = false;
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes) {
			if (changes.active) {
				this.popoverService.active = changes.active.currentValue;
				this.isOpen = this.active;
			}
		}
	}

	toggle(event) {
		// this.popoverService.$_Popover_toggle(event, this.content);
		this.isOpen = !this.isOpen;
	}

	hoverToggle(toggle: boolean) {
		// this.popoverService.$_Popover_hoverToggle(toggle);
		if (this.hover) {
			setTimeout(() => {
				this.isOpen = toggle;
				this.cdr.markForCheck();
				// this.$_Popover_reset();
			}, 250);
		}
	}

	/* returns one of the transition types supported by the transition component */
	transitionGen(): string {
		let type = null;
		if (this.transition === 'opacity-transform') {
			if (this.position === 'left') {
				type = 'right';
			} else if (this.position === 'right') {
				type = 'left';
			} else if (this.position === 'top') {
				type = 'up';
			} else {
				type = 'down';
			}
		}
		return type ? `opacity-${type}` : 'opacity';
	}

	/* tooltip wrapper classes */
	classGen() {
		return [
			this.position ? `tooltip-${this.position}` : 'tooltip-bottom',
			this.size === 'small' && !this.title && !this.contentSlotExists ? 'tooltip-small' : '',
			this.size === 'large' || this.title || this.contentSlotExists ? 'tooltip-large' : '',
			this.popoverService.overflowLeft ? 'tooltip-overflow' : '',
			this.positionX === 'left' ? 'tooltip-flush-left' : '',
			this.positionX === 'right' ? 'tooltip-flush-right' : ''
		];
	}
}
